import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import {
  ACCOUNT_TYPE,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  VENDOR_TYPE,
} from "@miplatform/constants";
import { mdiOpenInNew } from "@mdi/js";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AddHomeDrawer } from "../../../components/AddHomeDrawer/AddHomeDrawer";
import { AddAccount } from "../../Accounts/AddAccount/AddAccount";
import {
  ACCOUNT_PAGE_TYPE,
  BUILDER,
  SUPPLIER,
} from "../../Accounts/accountsConstants";
import { ConfigPageLayout } from "../../Config/ConfigPageLayout";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { useImportQuickBooksCustomersViewModel } from "./useImportQuickBooksCustomersViewModel";

export function ImportQuickBooksCustomers() {
  const { classes } = useStyles();
  const [isHomeDrawerOpen, setIsHomeDrawerOpen] = useState(false);
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const { isLoading, customers, getCustomersForImport } =
    useImportQuickBooksCustomersViewModel();

  useEffect(() => {
    getCustomersForImport();
  }, []);

  // TODO : match city on name from cities table for home creation
  // TODO : make community optional when importing home
  // TODO : try to lookup customer from the home and qbo audits to prepopulate

  const getCustomerIsSynced = (customer) => {
    return customer.hasMiBuildMatch && customer.miBuildId;
  };

  const handleImportAndReviewClick = (customer) => {
    setSelectedCustomer(customer);
    if (customer.type === "Account" || customer.type === "Vendor") {
      setIsAccountDrawerOpen(true);
    } else if (customer.type === "Home") {
      setIsHomeDrawerOpen(true);
    }
  };

  const getAccountPageType = (customer) => {
    // defaulting customer to builder (could also be service)
    if (customer.type === "Account") {
      return {
        pageType: ACCOUNT_PAGE_TYPE.CUSTOMER,
        tab: BUILDER,
        accountType: ACCOUNT_TYPE.BUILDER,
      };
    }
    // defaulting vendor to supplier (could also be subcontractor)
    if (customer.type === "Vendor") {
      return {
        pageType: ACCOUNT_PAGE_TYPE.VENDOR,
        tab: SUPPLIER,
        accountType: ACCOUNT_TYPE.SUPPLIER,
      };
    }
    return {};
  };

  const getDefaultAccountCode = (customer) => {
    return customer?.quickBooksDisplayName?.slice(0, 4).toUpperCase();
  };

  const handleHomeDrawerClose = () => {
    setIsHomeDrawerOpen(false);
    setSelectedCustomer(null);
  };

  const handleAccountDrawerClose = () => {
    setIsAccountDrawerOpen(false);
    setSelectedCustomer(null);
  };

  const handleAfterCreation = () => {
    getCustomersForImport();
  };

  const mapQuickBooksAddress = (qboAddr) => {
    return {
      addressLine1: qboAddr?.addressLine1 || "",
      addressLine2: qboAddr?.addressLine2 || "",
      city: qboAddr?.city || "",
      zip: qboAddr?.postalCode || "",
      state: qboAddr?.regionCode || "",
    };
  };

  const isValidQuickBooksUrl = (url) => {
    try {
      const urlObj = new URL(url);
      // Validate that the URL is from intuit.com or qbo.intuit.com domain
      const hostname = urlObj.hostname.toLowerCase();
      return hostname === "intuit.com" || hostname.endsWith(".intuit.com");
    } catch {
      return false;
    }
  };

  const handleQuickBooksLinkClick = (url) => {
    if (isValidQuickBooksUrl(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <ConfigPageLayout
      title="Import QuickBooks Customers"
      loading={isLoading}
      hideOnBack
    >
      <Box className={classes.contentContainer}>
        <ConfigAccordionHeader>
          {isLoading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          )}

          {!isLoading && customers.length === 0 && (
            <Typography>No QuickBooks customers found to import.</Typography>
          )}

          {!isLoading && customers.length > 0 && (
            <TableContainer className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight="600">Display Name</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="600">Type</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="600">MiBuild</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="600">QuickBooks</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => {
                    const isSynced = getCustomerIsSynced(customer);
                    return (
                      <TableRow key={customer.quickBooksId}>
                        <TableCell>
                          <Box>
                            <Typography fontWeight="500">
                              {customer.quickBooksDisplayName}
                            </Typography>
                            {customer.quickBooksFullyQualifiedName && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {customer.quickBooksFullyQualifiedName}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography>{customer.type}</Typography>
                        </TableCell>
                        <TableCell>
                          {isSynced ? (
                            <TableCell>
                              <Typography>Already Synced</Typography>
                            </TableCell>
                          ) : (
                            <MiButton
                              variant={BUTTON_VARIANTS.CONTAINED}
                              color={BUTTON_COLORS.PRIMARY}
                              onClick={() =>
                                handleImportAndReviewClick(customer)
                              }
                            >
                              Import
                            </MiButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <MiButton
                            variant={BUTTON_VARIANTS.TEXT}
                            color={BUTTON_COLORS.PRIMARY}
                            endIcon={{ path: mdiOpenInNew, iconSize: "16px" }}
                            onClick={() =>
                              handleQuickBooksLinkClick(customer.quickBooksLink)
                            }
                          >
                            View in QuickBooks
                          </MiButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ConfigAccordionHeader>
      </Box>

      <AddHomeDrawer
        open={isHomeDrawerOpen}
        onClose={handleHomeDrawerClose}
        onCreate={handleAfterCreation}
        quickBooksId={selectedCustomer?.quickBooksId}
        initialAddress={mapQuickBooksAddress(selectedCustomer?.address)}
      />

      {isAccountDrawerOpen && (
        <AddAccount
          type={getAccountPageType(selectedCustomer).pageType}
          tab={getAccountPageType(selectedCustomer).tab}
          showDrawer={isAccountDrawerOpen}
          onCloseDrawer={handleAccountDrawerClose}
          onRefresh={handleAfterCreation}
          initialData={{
            name: selectedCustomer?.quickBooksDisplayName,
            accountType: getAccountPageType(selectedCustomer).accountType,
            address: selectedCustomer?.address,
            billingAddress: selectedCustomer?.billingAddress,
            quickBooksId: selectedCustomer?.quickBooksId,
            ...(selectedCustomer?.type === "Vendor" && {
              supplierType: VENDOR_TYPE.OTHER,
            }),
            code: getDefaultAccountCode(selectedCustomer),
          }}
        />
      )}
    </ConfigPageLayout>
  );
}

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(3),
  },
  tableContainer: {
    width: "100%",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
}));
